/**
 * The cart, and what it costs — worked out on the screen so the cashier can read a total
 * before the sale is sent.
 *
 * Every figure in here is a *quote*. The server re-prices the whole cart at checkout from
 * the catalog and the tax rules in force, and the sale it returns is what goes on the
 * receipt and in the ledger. Two things follow: nothing computed here is ever posted back
 * as a total (the request carries quantities and discounts, never prices), and this file
 * mirrors the server's arithmetic closely enough that the number on the customer display
 * matches the one they are charged.
 *
 * Money is held in whole cents as integers. A cart is a running sum of small amounts, and
 * `0.1 + 0.2` is the classic way to hand somebody the wrong change.
 */

import type { TillProduct } from '@/features/pos/api'

export interface CartLine {
  product: TillProduct
  quantity: number
  /** Money off this line, in cents. Not a percentage — see `discountFromPercent`. */
  discountCents: number
}

export interface PricedLine extends CartLine {
  /** Quantity times price, before any discount. */
  grossCents: number
  /** This line's share of the sale-level discount, on top of its own. */
  shareCents: number
  /** What is actually taxed: gross, less both discounts. */
  taxableCents: number
  taxCents: number
  /** Taxable plus tax. What the customer pays for this line. */
  totalCents: number
}

export interface CartTotals {
  lines: PricedLine[]
  itemCount: number
  /** Sum of the taxable amounts — the same field the API calls `subtotal`. */
  subtotalCents: number
  /** Everything taken off, line and sale discounts together. */
  discountCents: number
  taxCents: number
  /** Signed, under a unit of currency either way. Zero where the regime rounds nothing. */
  roundOffCents: number
  totalCents: number
  /** One row per rate charged, for the foot of the screen. */
  taxBands: { rate: string; taxableCents: number; taxCents: number }[]
}

// --- cents ------------------------------------------------------------------------------

/** `"120.50"` -> `12050`. Anything unreadable is zero, which is what a blank field means. */
export function toCents(value: string | number | null | undefined): number {
  if (value == null || value === '') return 0
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return 0
  return Math.round(parsed * 100)
}

/** `12050` -> `"120.50"`. The form the API wants: a decimal string, two places. */
export function fromCents(cents: number): string {
  return (cents / 100).toFixed(2)
}

/** Half-up on a positive amount, matching the server's `ROUND_HALF_UP` on money. */
function roundCents(value: number): number {
  return Math.sign(value) * Math.round(Math.abs(value))
}

/**
 * Split `amount` across `weights` proportionally, losing nothing.
 *
 * The remainder goes on the largest weight, exactly as `src.platform.money.allocate` does
 * it server-side. Which line carries the odd cent is arbitrary; agreeing with the server
 * about *which* is what keeps the quoted line totals equal to the printed ones.
 */
export function allocate(amountCents: number, weights: number[]): number[] {
  const total = weights.reduce((sum, weight) => sum + weight, 0)
  if (amountCents === 0 || total === 0) return weights.map(() => 0)

  const shares = weights.map((weight) => roundCents((amountCents * weight) / total))
  const drift = amountCents - shares.reduce((sum, share) => sum + share, 0)
  if (drift !== 0) {
    let biggest = 0
    for (let i = 1; i < weights.length; i += 1) if (weights[i] > weights[biggest]) biggest = i
    shares[biggest] += drift
  }
  return shares
}

// --- the cart ---------------------------------------------------------------------------

/**
 * Add a scan to the cart, merging into the line that product already has.
 *
 * Merging rather than appending is what a scanner expects: three passes of the same
 * barcode is a quantity of three, not three lines of one, and a receipt listing the same
 * item three times is the kind of thing customers query at the counter.
 */
export function addToCart(lines: CartLine[], product: TillProduct, quantity = 1): CartLine[] {
  const existing = lines.findIndex((line) => line.product.id === product.id)
  if (existing === -1) return [...lines, { product, quantity, discountCents: 0 }]
  return lines.map((line, index) =>
    index === existing ? { ...line, quantity: line.quantity + quantity } : line,
  )
}

/** Quantity of zero or less removes the line: the cashier scanned it by mistake. */
export function setQuantity(lines: CartLine[], productId: string, quantity: number): CartLine[] {
  if (quantity <= 0) return removeLine(lines, productId)
  return lines.map((line) =>
    line.product.id === productId ? { ...line, quantity: Math.min(quantity, 10_000) } : line,
  )
}

export function removeLine(lines: CartLine[], productId: string): CartLine[] {
  return lines.filter((line) => line.product.id !== productId)
}

/**
 * The product's own standing discount on this line, in cents. Comes off before anything the
 * cashier types, and is carried as part of the line discount on the receipt — the same way
 * the server does it.
 */
export function promoCents(line: CartLine): number {
  const percent = Number(line.product.promo_pct || 0)
  if (!Number.isFinite(percent) || percent <= 0) return 0
  return roundCents((lineGrossCents(line) * percent) / 100)
}

/** Capped at what is left of the line after its promo — the server rejects more than that. */
export function setLineDiscount(
  lines: CartLine[],
  productId: string,
  discountCents: number,
): CartLine[] {
  return lines.map((line) => {
    if (line.product.id !== productId) return line
    const room = lineGrossCents(line) - promoCents(line)
    return { ...line, discountCents: Math.max(0, Math.min(discountCents, room)) }
  })
}

/**
 * A percentage as an amount, because a percentage is what the cashier types and an amount
 * is what the receipt has to add up to. Converting once here rather than on every read is
 * what keeps the line total and the ledger equal.
 */
export function discountFromPercent(baseCents: number, percent: number): number {
  if (!Number.isFinite(percent) || percent <= 0) return 0
  return Math.min(baseCents, roundCents((baseCents * percent) / 100))
}

export function lineGrossCents(line: CartLine): number {
  return roundCents(toCents(line.product.price) * line.quantity)
}

/**
 * Price the cart.
 *
 * The sale-level discount is spread across the lines in proportion to what each is worth
 * *after* its own discount, so "10% off, and 20 off the rice" is the two in the order the
 * cashier said them — and so the tax is right: 20 off a basket of 5% and 18% goods is not
 * 20 off either rate.
 *
 * `roundsToWholeCurrency` comes from the regime, never from a constant here. India rounds
 * a counter total to the rupee; the US rounds nothing at all (ADR-013).
 */
export function priceCart(
  lines: CartLine[],
  saleDiscountCents = 0,
  roundsToWholeCurrency = false,
): CartTotals {
  const afterLine = lines.map((line) => {
    const room = Math.max(0, lineGrossCents(line) - promoCents(line))
    return Math.max(0, room - Math.min(line.discountCents, room))
  })
  const sellable = afterLine.reduce((sum, value) => sum + value, 0)
  const discount = Math.max(0, Math.min(saleDiscountCents, sellable))
  const shares = allocate(discount, afterLine)

  const priced: PricedLine[] = lines.map((line, index) => {
    const grossCents = lineGrossCents(line)
    const taxableCents = Math.max(0, afterLine[index] - shares[index])
    const taxCents = roundCents((taxableCents * Number(line.product.tax_rate || 0)) / 100)
    return {
      ...line,
      grossCents,
      shareCents: shares[index],
      taxableCents,
      taxCents,
      totalCents: taxableCents + taxCents,
    }
  })

  const subtotalCents = priced.reduce((sum, line) => sum + line.taxableCents, 0)
  const taxCents = priced.reduce((sum, line) => sum + line.taxCents, 0)
  const payable = subtotalCents + taxCents
  const totalCents = roundsToWholeCurrency ? Math.round(payable / 100) * 100 : payable

  const bands = new Map<string, { rate: string; taxableCents: number; taxCents: number }>()
  for (const line of priced) {
    if (line.taxCents === 0) continue
    const rate = String(Number(line.product.tax_rate || 0))
    const band = bands.get(rate) ?? { rate, taxableCents: 0, taxCents: 0 }
    band.taxableCents += line.taxableCents
    band.taxCents += line.taxCents
    bands.set(rate, band)
  }

  return {
    lines: priced,
    itemCount: lines.reduce((sum, line) => sum + line.quantity, 0),
    subtotalCents,
    discountCents: priced.reduce(
      (sum, line) => sum + (line.grossCents - line.taxableCents),
      0,
    ),
    taxCents,
    roundOffCents: totalCents - payable,
    totalCents,
    taxBands: [...bands.values()].sort((a, b) => Number(a.rate) - Number(b.rate)),
  }
}

// --- the scan field ---------------------------------------------------------------------

export interface ParsedScan {
  code: string
  quantity: number
}

/**
 * Read what was typed or scanned into the one field the till has.
 *
 * `12*RICE-5KG` means twelve of it. The multiplier is the oldest keyboard shortcut on a
 * counter and it is why a cashier selling a dozen of something never touches the mouse:
 * they type the count, star, then scan. `x` works too, because half the trade writes it
 * that way, and a bare code is a quantity of one.
 *
 * A scanner sends the barcode followed by Enter and nothing else, so a plain scan takes
 * the same path with nothing special about it.
 */
export function parseScan(raw: string): ParsedScan | null {
  const input = raw.trim()
  if (!input) return null

  const multiplied = /^(\d{1,4})\s*[*x]\s*(.+)$/i.exec(input)
  if (multiplied) {
    const quantity = Number(multiplied[1])
    const code = multiplied[2].trim()
    if (!code || quantity <= 0) return null
    return { code, quantity }
  }
  // A multiplier with nothing after it yet: the cashier is mid-keystroke, and looking
  // "12*" up in the catalog would sound the not-found beep at somebody typing correctly.
  if (/^\d{1,4}\s*[*x]\s*$/i.test(input)) return null

  return { code: input, quantity: 1 }
}

// --- tenders ------------------------------------------------------------------------------

export interface TenderDraft {
  method: 'cash' | 'card' | 'store_credit'
  /** As typed, in the currency's own units. Empty while the cashier is still typing. */
  amount: string
  reference: string
}

/**
 * What is still owed once the tenders entered so far are counted. Negative means the
 * cashier has over-tendered — which is only legal in cash, and becomes change.
 */
export function outstandingCents(totalCents: number, tenders: TenderDraft[]): number {
  return totalCents - tenders.reduce((sum, tender) => sum + toCents(tender.amount), 0)
}

/**
 * Cash handed over, less what the sale needs, is change.
 *
 * Worked out here for the drawer only. The request sends what was handed over and the
 * server states the change: a client that can assert the change is a client that can get
 * it wrong, and the number that has to reconcile is the server's.
 */
export function changeCents(totalCents: number, tenders: TenderDraft[]): number {
  const cash = tenders
    .filter((tender) => tender.method === 'cash')
    .reduce((sum, tender) => sum + toCents(tender.amount), 0)
  const nonCash = tenders
    .filter((tender) => tender.method !== 'cash')
    .reduce((sum, tender) => sum + toCents(tender.amount), 0)
  return Math.max(0, cash - Math.max(0, totalCents - nonCash))
}

/**
 * Why this split cannot be sent yet, or null if it can.
 *
 * Checked before the request rather than after the rejection because the customer is at
 * the counter: an error the cashier can fix in the dialog they are already in beats a 422
 * that empties it.
 */
export function tenderProblem(
  totalCents: number,
  tenders: TenderDraft[],
  storeCreditCents: number | null,
): string | null {
  const entered = tenders.filter((tender) => toCents(tender.amount) > 0)
  if (entered.length === 0) return 'Enter what the customer is paying with.'

  const methods = entered.map((tender) => tender.method)
  if (new Set(methods).size !== methods.length) {
    return 'Each payment method may appear once on a sale.'
  }

  const settled = entered.reduce((sum, tender) => sum + toCents(tender.amount), 0)
  const cash = entered
    .filter((tender) => tender.method === 'cash')
    .reduce((sum, tender) => sum + toCents(tender.amount), 0)
  // Over-tendering is cash being handed over, and only cash: a card charged for more than
  // the sale is a card charged wrongly, and store credit taken beyond the total is spent.
  if (settled > totalCents && settled - cash > totalCents) {
    return 'Only cash may be over-tendered. Reduce the card or store credit amount.'
  }
  if (settled < totalCents) return 'That does not cover the sale yet.'

  const credit = entered.find((tender) => tender.method === 'store_credit')
  if (credit) {
    if (storeCreditCents == null) return 'Attach a customer before taking store credit.'
    if (toCents(credit.amount) > storeCreditCents) {
      return 'That is more store credit than the customer has.'
    }
  }
  return null
}

/**
 * The tenders as the API takes them: what each method *settles*, never what was handed
 * over. Cash is capped at the balance the other methods left, and the excess is change.
 */
export function settlementTenders(
  totalCents: number,
  tenders: TenderDraft[],
): { method: 'cash' | 'card' | 'store_credit'; amount: string; reference?: string }[] {
  const entered = tenders.filter((tender) => toCents(tender.amount) > 0)
  const nonCash = entered.filter((tender) => tender.method !== 'cash')
  const settledByOthers = nonCash.reduce((sum, tender) => sum + toCents(tender.amount), 0)

  return entered
    .map((tender) => {
      const amount =
        tender.method === 'cash'
          ? Math.min(toCents(tender.amount), Math.max(0, totalCents - settledByOthers))
          : toCents(tender.amount)
      return {
        method: tender.method,
        amount: fromCents(amount),
        ...(tender.reference.trim() ? { reference: tender.reference.trim() } : {}),
      }
    })
    .filter((tender) => toCents(tender.amount) > 0)
}
